import { Component, EventEmitter, inject, Input, OnInit, Output, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatInputModule } from '@angular/material/input';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { provideNativeDateAdapter } from '@angular/material/core';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { CreatePoolParkingRequest, OrganizationService, ParkingTypeService, ParkingSpaceDto, OrganizationDto, ListOrganizationRequest, ParkingZoneDto } from '../../../../../api/gen';

export interface PoolParkingResult {
    success: boolean;
    data?: any;
}

@Component({
    selector: 'app-add-pool-parking',
    imports: [
        CommonModule,
        ReactiveFormsModule,
        MatFormFieldModule,
        MatSelectModule,
        MatInputModule,
        MatDatepickerModule,
        MatIconModule,
        MatProgressSpinnerModule,
        MatSnackBarModule
    ],
    providers: [provideNativeDateAdapter()],
    templateUrl: './add-pool-parking.component.html',
    styleUrl: './add-pool-parking.component.scss'
})
export class AddPoolParkingComponent implements OnInit {
    private fb = inject(FormBuilder);
    private parkingTypeService = inject(ParkingTypeService);
    private organizationService = inject(OrganizationService);
    private snackBar = inject(MatSnackBar);

    @Input({ required: true }) parkingOperatorId: string | undefined;
    @Input() parkingSpace!: ParkingSpaceDto;
    @Output() submitted = new EventEmitter<PoolParkingResult>();

    form!: FormGroup;
    isLoading = signal(false);
    isLoadingOrgs = signal(false);
    availableOrganizations = signal<OrganizationDto[]>([]);
    availableZones = signal<ParkingZoneDto[]>([]);

    ngOnInit(): void {
        this.initForm();
        this.loadOrganizations();
        this.availableZones.set(this.parkingSpace?.parkingZones || []);
    }

    private initForm(): void {
        this.form = this.fb.group({
            organizationId: [null],
            availableForParkingZones: [[]],
            availableFrom: [null],
            availableTo: [null]
        });
    }

    private loadOrganizations(): void {
        this.isLoadingOrgs.set(true);
        const request: ListOrganizationRequest = {
            pageSize: 100,
            pageIndex: 0
        };
        this.organizationService.organizationsFilterPost(request).subscribe({
            next: (result) => {
                this.availableOrganizations.set(result.items || []);
                this.isLoadingOrgs.set(false);
            },
            error: () => {
                this.isLoadingOrgs.set(false);
            }
        });
    }

    submit(): void {
        this.isLoading.set(true);

        const formValue = this.form.value;
        const request: CreatePoolParkingRequest = {
            availableFrom: formValue.availableFrom ? new Date(formValue.availableFrom).toISOString() : null,
            availableTo: formValue.availableTo ? new Date(formValue.availableTo).toISOString() : null,
            availableForParkingSpaces: this.parkingSpace?.id ? [this.parkingSpace.id] : null,
            availableForParkingZones: formValue.availableForParkingZones?.length > 0 ? formValue.availableForParkingZones : null,
            organizationId: formValue.organizationId || null
        };

        this.parkingTypeService.parkingTypesPoolParkingPost(this.parkingOperatorId, request).subscribe({
            next: (result) => {
                this.snackBar.open('Pool parking type created successfully', 'Close', { duration: 3000 });
                this.isLoading.set(false);
                this.submitted.emit({ success: true, data: result });
            },
            error: (error) => {
                console.error('Error creating pool parking type:', error);
                this.snackBar.open('Failed to create pool parking type', 'Close', { duration: 3000 });
                this.isLoading.set(false);
                this.submitted.emit({ success: false });
            }
        });
    }
}
